import type { CharacterBond } from '../../domain/types.js';

export interface BondPairSide {
  sex: 'M' | 'F';
  species: string;
  ageRange: string;
}

export interface BondOption {
  kind: string;
  label: string;
}

const MINOR_RE = /child|kid|teen|youth|minor/i;

function isHuman(species: string): boolean {
  return species.trim().toLowerCase() === 'human';
}

function isMinor(ageRange: string): boolean {
  return MINOR_RE.test(ageRange);
}

function genderedTargetRoles(sex: 'M' | 'F', allowRomantic: boolean): BondOption[] {
  const romantic: BondOption[] = allowRomantic
    ? sex === 'M'
      ? [
          { kind: 'boyfriend', label: 'Boyfriend' },
          { kind: 'husband', label: 'Husband' },
          { kind: 'work-husband', label: 'Work husband' },
        ]
      : [
          { kind: 'girlfriend', label: 'Girlfriend' },
          { kind: 'wife', label: 'Wife' },
          { kind: 'work-wife', label: 'Work wife' },
        ]
    : [];
  const family: BondOption[] = sex === 'M'
    ? [
        { kind: 'brother', label: 'Brother' },
        { kind: 'step-brother', label: 'Step-brother' },
        { kind: 'father', label: 'Father' },
        { kind: 'uncle', label: 'Uncle' },
        { kind: 'nephew', label: 'Nephew' },
        { kind: 'son', label: 'Son' },
      ]
    : [
        { kind: 'sister', label: 'Sister' },
        { kind: 'step-sister', label: 'Step-sister' },
        { kind: 'mother', label: 'Mother' },
        { kind: 'aunt', label: 'Aunt' },
        { kind: 'niece', label: 'Niece' },
        { kind: 'daughter', label: 'Daughter' },
      ];
  return [...romantic, ...family];
}

const STRANGER: BondOption = { kind: 'stranger', label: 'Stranger' };

const HUMAN_COMMON: BondOption[] = [
  { kind: 'friend', label: 'Friend' },
  { kind: 'co-worker', label: 'Co-worker' },
  { kind: 'co-worker-friend', label: 'Co-worker friend' },
  { kind: 'cousin', label: 'Cousin' },
];

const NON_HUMAN: BondOption[] = [
  { kind: 'friend', label: 'Friend' },
  { kind: 'companion', label: 'Companion' },
  { kind: 'guardian', label: 'Guardian' },
  { kind: 'packmate', label: 'Packmate' },
  { kind: 'littermate', label: 'Littermate' },
  { kind: 'rival', label: 'Rival' },
];

/** Bond kinds available when dragging from source toward target. */
export function bondOptionsForPair(source: BondPairSide, target: BondPairSide): BondOption[] {
  const bothHuman = isHuman(source.species) && isHuman(target.species);
  if (!bothHuman) {
    return [STRANGER, ...NON_HUMAN];
  }
  const eitherMinor = isMinor(source.ageRange) || isMinor(target.ageRange);
  const allowRomantic = !eitherMinor;
  return [
    STRANGER,
    ...HUMAN_COMMON,
    ...genderedTargetRoles(target.sex, allowRomantic),
  ];
}

export function bondQuestionCopy(sourceName: string, targetName: string): string {
  return `What is ${targetName} to ${sourceName}?`;
}

const SYMMETRIC = new Set([
  'stranger', 'friend', 'co-worker', 'co-worker-friend', 'cousin',
  'companion', 'packmate', 'littermate', 'rival',
]);

/** Reverse bond kind for the opposite direction. */
export function inverseKind(kind: string, otherSex: 'M' | 'F'): string {
  if (SYMMETRIC.has(kind)) return kind;
  switch (kind) {
    case 'boyfriend': return otherSex === 'M' ? 'boyfriend' : 'girlfriend';
    case 'girlfriend': return otherSex === 'M' ? 'boyfriend' : 'girlfriend';
    case 'husband': return otherSex === 'M' ? 'husband' : 'wife';
    case 'wife': return otherSex === 'M' ? 'husband' : 'wife';
    case 'work-husband': return otherSex === 'M' ? 'work-husband' : 'work-wife';
    case 'work-wife': return otherSex === 'M' ? 'work-husband' : 'work-wife';
    case 'brother': return otherSex === 'M' ? 'brother' : 'sister';
    case 'sister': return otherSex === 'M' ? 'brother' : 'sister';
    case 'step-brother': return otherSex === 'M' ? 'step-brother' : 'step-sister';
    case 'step-sister': return otherSex === 'M' ? 'step-brother' : 'step-sister';
    case 'father': return otherSex === 'M' ? 'son' : 'daughter';
    case 'mother': return otherSex === 'M' ? 'son' : 'daughter';
    case 'son': return otherSex === 'M' ? 'father' : 'mother';
    case 'daughter': return otherSex === 'M' ? 'father' : 'mother';
    case 'uncle': return otherSex === 'M' ? 'nephew' : 'niece';
    case 'aunt': return otherSex === 'M' ? 'nephew' : 'niece';
    case 'nephew': return otherSex === 'M' ? 'uncle' : 'aunt';
    case 'niece': return otherSex === 'M' ? 'uncle' : 'aunt';
    case 'guardian': return 'guardian';
    default: return kind;
  }
}

function clampCloseness(n: number): number {
  return Math.max(0, Math.min(10, Math.round(n)));
}

function bondKey(fromId: string, toId: string): string {
  return `${fromId}>${toId}`;
}

function findBond(bonds: CharacterBond[], fromId: string, toId: string): CharacterBond | undefined {
  return bonds.find((b) => b.fromId === fromId && b.toId === toId);
}

/** Upsert a directional bond and sync the reverse kind without overwriting reverse closeness. */
export function upsertBond(
  bonds: CharacterBond[],
  fromId: string,
  toId: string,
  kind: string,
  closeness: number,
  fromSex: 'M' | 'F',
): CharacterBond[] {
  const next = bonds.filter((b) => !(b.fromId === fromId && b.toId === toId));
  const clamped = clampCloseness(closeness);
  if (kind !== 'stranger' || clamped > 0) {
    next.push({ fromId, toId, kind, closeness: clamped });
  }
  const reverseKind = inverseKind(kind, fromSex);
  const existingReverse = findBond(bonds, toId, fromId);
  const reverseCloseness = existingReverse?.closeness ?? 0;
  const withoutReverse = next.filter((b) => !(b.fromId === toId && b.toId === fromId));
  if (reverseKind !== 'stranger' || reverseCloseness > 0) {
    withoutReverse.push({
      fromId: toId,
      toId: fromId,
      kind: reverseKind,
      closeness: reverseCloseness,
    });
  }
  return withoutReverse;
}

const LABEL_BY_KIND: Record<string, string> = {
  stranger: 'Stranger',
  friend: 'Friend',
  'co-worker': 'Co-worker',
  'co-worker-friend': 'Co-worker friend',
  cousin: 'Cousin',
  boyfriend: 'Boyfriend',
  girlfriend: 'Girlfriend',
  husband: 'Husband',
  wife: 'Wife',
  'work-husband': 'Work husband',
  'work-wife': 'Work wife',
  brother: 'Brother',
  sister: 'Sister',
  'step-brother': 'Step-brother',
  'step-sister': 'Step-sister',
  father: 'Father',
  mother: 'Mother',
  son: 'Son',
  daughter: 'Daughter',
  uncle: 'Uncle',
  aunt: 'Aunt',
  nephew: 'Nephew',
  niece: 'Niece',
  companion: 'Companion',
  guardian: 'Guardian',
  packmate: 'Packmate',
  littermate: 'Littermate',
  rival: 'Rival',
};

export function bondLabel(kind: string): string {
  return LABEL_BY_KIND[kind] ?? kind;
}

/** True when the bond is unset or explicitly stranger at zero closeness. */
export function isStrangerBond(bond: CharacterBond | undefined): boolean {
  if (!bond) return true;
  return bond.kind === 'stranger' && bond.closeness === 0;
}

export function outgoingBonds(
  bonds: CharacterBond[],
  fromId: string,
): CharacterBond[] {
  return bonds.filter((b) => b.fromId === fromId && !isStrangerBond(b));
}

export { bondKey };
